import React, { useState, useEffect } from "react";
import styles from "./BillingAddressForm.module.scss";
import { BillingAddress, BillingAddressData } from "@models/billing-address";
import { countries } from "@utils/countries";
import { getStatesForCountry, hasStates } from "@utils/states";
import Loader from "@components/Loader/Loader";

interface BillingAddressFormProps {
  initialData?: BillingAddress | null;
  onSubmit: (data: BillingAddressData) => Promise<void>;
  onCancel?: () => void;
  submitButtonText?: string;
  showCancelButton?: boolean;
}

const BillingAddressForm: React.FC<BillingAddressFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  submitButtonText = "Save",
  showCancelButton = false,
}) => {
  const [formData, setFormData] = useState<BillingAddressData>({
    name: initialData?.name || "",
    country: initialData?.country || "",
    state: initialData?.state || "",
    city: initialData?.city || "",
    street_address: initialData?.street_address || "",
    pin_code: initialData?.pin_code || "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof BillingAddressData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableStates, setAvailableStates] = useState<string[]>([]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || "",
        country: initialData.country || "",
        state: initialData.state || "",
        city: initialData.city || "",
        street_address: initialData.street_address || "",
        pin_code: initialData.pin_code || "",
      });
      
      // Load states if country is set
      if (initialData.country) {
        const states = getStatesForCountry(initialData.country);
        setAvailableStates(states);
      }
    }
  }, [initialData]);

  // Update states when country changes
  useEffect(() => {
    if (formData.country) {
      const states = getStatesForCountry(formData.country);
      setAvailableStates(states);
      
      // Clear state if it's not available for the new country
      setFormData((prev) => {
        if (prev.state && !states.includes(prev.state)) {
          return { ...prev, state: "" };
        }
        return prev;
      });
    } else {
      setAvailableStates([]);
      setFormData((prev) => ({ ...prev, state: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.country]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof BillingAddressData, string>> = {};

    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Name is required";
    } else if (formData.name.length > 50) {
      newErrors.name = "Name must be 50 characters or less";
    }

    if (!formData.country || formData.country.trim() === "") {
      newErrors.country = "Country is required";
    }

    // Validate state if country has states
    if (formData.country && hasStates(formData.country)) {
      if (!formData.state || formData.state.trim() === "") {
        newErrors.state = "State is required";
      }
    }

    if (!formData.city || formData.city.trim() === "") {
      newErrors.city = "City is required";
    } else if (formData.city.length > 50) {
      newErrors.city = "City must be 50 characters or less";
    }

    if (!formData.street_address || formData.street_address.trim() === "") {
      newErrors.street_address = "Street address is required";
    } else if (formData.street_address.length > 100) {
      newErrors.street_address = "Street address must be 100 characters or less";
    }

    if (!formData.pin_code || formData.pin_code.trim() === "") {
      newErrors.pin_code = "Pin code is required";
    } else if (formData.pin_code.length > 20) {
      newErrors.pin_code = "Pin code must be 20 characters or less";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    field: keyof BillingAddressData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error("Error submitting billing address:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label htmlFor="name" className={styles.label}>
            Name <span className={styles.required}>*</span>
          </label>
          <input
            type="text"
            id="name"
            className={`${styles.input} ${errors.name ? styles.inputError : ""}`}
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            maxLength={50}
            placeholder="Enter your full name"
            disabled={isSubmitting}
          />
          {errors.name && <span className={styles.errorText}>{errors.name}</span>}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="country" className={styles.label}>
            Country <span className={styles.required}>*</span>
          </label>
          <select
            id="country"
            className={`${styles.select} ${errors.country ? styles.inputError : ""}`}
            value={formData.country}
            onChange={(e) => handleChange("country", e.target.value)}
            disabled={isSubmitting}
          >
            <option value="">Select a country</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
          {errors.country && <span className={styles.errorText}>{errors.country}</span>}
        </div>
      </div>

      {formData.country && hasStates(formData.country) && (
        <div className={styles.formGroup}>
          <label htmlFor="state" className={styles.label}>
            State <span className={styles.required}>*</span>
          </label>
          <select
            id="state"
            className={`${styles.select} ${errors.state ? styles.inputError : ""}`}
            value={formData.state}
            onChange={(e) => handleChange("state", e.target.value)}
            disabled={isSubmitting}
          >
            <option value="">Select a state</option>
            {availableStates.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
          {errors.state && <span className={styles.errorText}>{errors.state}</span>}
        </div>
      )}

      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label htmlFor="city" className={styles.label}>
            City <span className={styles.required}>*</span>
          </label>
          <input
            type="text"
            id="city"
            className={`${styles.input} ${errors.city ? styles.inputError : ""}`}
            value={formData.city}
            onChange={(e) => handleChange("city", e.target.value)}
            maxLength={50}
            placeholder="Enter your city"
            disabled={isSubmitting}
          />
          {errors.city && <span className={styles.errorText}>{errors.city}</span>}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="pin_code" className={styles.label}>
            Pin Code <span className={styles.required}>*</span>
          </label>
          <input
            type="text"
            id="pin_code"
            className={`${styles.input} ${errors.pin_code ? styles.inputError : ""}`}
            value={formData.pin_code}
            onChange={(e) => handleChange("pin_code", e.target.value)}
            maxLength={20}
            placeholder="Enter your pin code"
            disabled={isSubmitting}
          />
          {errors.pin_code && <span className={styles.errorText}>{errors.pin_code}</span>}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="street_address" className={styles.label}>
          Street Address <span className={styles.required}>*</span>
        </label>
        <textarea
          id="street_address"
          className={`${styles.textarea} ${errors.street_address ? styles.inputError : ""}`}
          value={formData.street_address}
          onChange={(e) => handleChange("street_address", e.target.value)}
          maxLength={100}
          rows={2}
          placeholder="Enter your street address"
          disabled={isSubmitting}
        />
        <div className={styles.charCount}>
          {formData.street_address?.length || 0}/100 characters
        </div>
        {errors.street_address && (
          <span className={styles.errorText}>{errors.street_address}</span>
        )}
      </div>

      <div className={styles.buttonGroup}>
        {showCancelButton && onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className={styles.cancelButton}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className={styles.submitButton}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader size={16} color="#fff" />
              <span>Saving...</span>
            </>
          ) : (
            submitButtonText
          )}
        </button>
      </div>
    </form>
  );
};

export default BillingAddressForm;

